import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, TextField, Typography } from "@mui/material";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import TriggerPoints from "../../components/TriggerPoints";
import { getStringParamsFromObject } from "../../utils";
import { INDIGO, SLATE } from "../../constants/Colors";

const TEXTAREA_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900], lineHeight: 1.6 },
};

const INITIAL_STATE = {
  prompt_input_raw: "",
  disclaimer: "",
  id: null,
  app_id: null,
};

export default function NoGoTopics() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector((state) => state?.apps?.selectedAgentSiteCode);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [state, setState] = useState(INITIAL_STATE);
  const [triggerPoint, setTriggerPoint] = useState(selectedAgentSiteCode || "");
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const originalRef = useRef(null);

  useEffect(() => {
    setPageMeta({ description: "Define topics the AI agent should refuse to answer." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    const query = getStringParamsFromObject({
      app_id: currentApp.id,
      trigger_point: triggerPoint,
      types: "no_go_topics",
    });
    makeRequest(`/v1/controls?${query}`, { method: "GET", authTokens: tokens })
      .then((configs) => {
        let next = INITIAL_STATE;
        if (Array.isArray(configs) && configs.length > 0) {
          const c = configs[0];
          next = { prompt_input_raw: c.prompt_input_raw || "", disclaimer: c.metadata?.disclaimer || "", id: c.id, app_id: c.app_id };
        }
        originalRef.current = { prompt_input_raw: next.prompt_input_raw, disclaimer: next.disclaimer };
        setState(next);
        setIsDirty(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id, triggerPoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setState((prev) => {
      const next = { ...prev, [field]: value };
      const orig = originalRef.current;
      setIsDirty(orig === null || next.prompt_input_raw !== orig.prompt_input_raw || next.disclaimer !== orig.disclaimer);
      return next;
    });
  };

  const handleSave = async () => {
    const config = {
      internal_name: "no_go_topics",
      display_name: "No-Go topics list",
      prompt_input_raw: state.prompt_input_raw,
      trigger_point: triggerPoint,
      metadata: { disclaimer: state.disclaimer },
      ...(state.id ? { id: state.id } : {}),
    };

    setSaving(true);
    try {
      await makeRequest(`/v1/controls`, {
        method: "PUT",
        body: { controls: [config], app_id: currentApp?.id },
        authTokens: tokens,
      });
      originalRef.current = { prompt_input_raw: state.prompt_input_raw, disclaimer: state.disclaimer };
      setIsDirty(false);
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading No-GO Topics..." subMessage="Fetching configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {!selectedAgentSiteCode && currentApp?.trigger_points?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <TriggerPoints app={currentApp} onChange={setTriggerPoint} triggerPoint={triggerPoint} />
        </Box>
      )}

      {/* Topics list */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            No-GO Topics List
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], lineHeight: 1.5, mb: 2 }}>
            List of topics that the bot should not answer.
          </Typography>
          <TextField
            multiline
            minRows={8}
            maxRows={20}
            fullWidth
            value={state.prompt_input_raw}
            onChange={(e) => handleChange("prompt_input_raw", e.target.value)}
            placeholder={"1. E.g. Comparison with competing Universities\n2. E.g. Questions unrelated to education"}
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      {/* Canned response */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            Canned Response
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], lineHeight: 1.5, mb: 2 }}>
            The canned response for when users ask no-go topic questions.
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={state.disclaimer}
            onChange={(e) => handleChange("disclaimer", e.target.value)}
            placeholder="E.g. I do not have the answer to this question"
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      <BottomActionBar
        disabledSave={!isDirty || saving}
        handleSave={handleSave}
        saving={saving}
      />
    </Box>
  );
}
